'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

interface NavItem {
  label: string;
  href: string;
  icon: string;
  badge?: number;
}

const bottomNavItems: NavItem[] = [
  { label: 'Sign Out', href: '/sign-up-login-screen', icon: 'ArrowRightOnRectangleIcon' },
];

interface SidebarProps {
  activeRoute: string;
  sheikhUnreadCount?: number;
}

export default function Sidebar({ activeRoute, sheikhUnreadCount = 0 }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);

  const navItems: NavItem[] = [
    { label: 'Dashboard', href: '/dashboard', icon: 'HomeIcon' },
    { label: 'Prayer History', href: '/prayer-history', icon: 'BookOpenIcon' },
    { label: 'My Progress', href: '/my-progress', icon: 'ChartBarIcon' },
    { label: 'Prayer Groups', href: '/prayer-groups', icon: 'UserGroupIcon' },
    { label: 'Community', href: '/community', icon: 'UsersIcon' },
    { label: 'Shared Goals', href: '/shared-goals', icon: 'FlagIcon' },
    { label: 'Challenges', href: '/challenges', icon: 'BoltIcon' },
    { label: 'Community Rankings', href: '/community-rankings', icon: 'ChartBarIcon' },
    { label: 'Activity Stream', href: '/activity-stream', icon: 'RssIcon' },
    { label: 'Entrepreneur Hub', href: '/entrepreneur-hub', icon: 'RocketLaunchIcon' },
    { label: 'Freelance Consultants', href: '/freelance-consultants', icon: 'BriefcaseIcon' },
    { label: 'Browse Consultants', href: '/browse-consultants', icon: 'MagnifyingGlassCircleIcon' },
    { label: 'Book a Session', href: '/book-session', icon: 'CalendarIcon' },
    { label: 'My Bookings', href: '/my-bookings', icon: 'CalendarDaysIcon' },
    { label: 'My Sessions', href: '/my-sessions', icon: 'ClipboardDocumentListIcon' },
    { label: 'Order Receipt', href: '/order-receipt', icon: 'ReceiptRefundIcon' },
    { label: 'Video Call', href: '/video-call', icon: 'VideoCameraIcon' },
    { label: 'Video Session', href: '/video-session', icon: 'PlayCircleIcon' },
    { label: 'Recordings', href: '/consultation-recordings', icon: 'FilmIcon' },
    { label: 'Consultation Messages', href: '/consultation-messages', icon: 'ChatBubbleLeftEllipsisIcon' },
    { label: 'Session Reviews', href: '/session-reviews', icon: 'StarIcon' },
    { label: 'Submit a Review', href: '/submit-review', icon: 'PencilSquareIcon' },
    { label: 'All Consultant Reviews', href: '/consultant-reviews', icon: 'ClipboardDocumentListIcon' },
    { label: 'Sessions Dashboard', href: '/sessions-dashboard', icon: 'PresentationChartBarIcon' },
    { label: 'Consultant Dashboard', href: '/consultant-dashboard', icon: 'ChartBarSquareIcon' },
    { label: 'Edit My Profile', href: '/consultant-profile-edit', icon: 'PencilSquareIcon' },
    { label: 'Manage Availability', href: '/consultant-availability', icon: 'CalendarDaysIcon' },
    { label: 'Wallet & Withdrawals', href: '/consultant-withdrawals', icon: 'BanknotesIcon' },
    { label: 'Consultant Earnings', href: '/consultant-earnings', icon: 'CurrencyDollarIcon' },
    { label: 'Become a Consultant', href: '/consultant-onboarding', icon: 'UserPlusIcon' },
    { label: 'Messages', href: '/messages', icon: 'ChatBubbleLeftRightIcon' },
    { label: 'Achievements', href: '/achievements', icon: 'TrophyIcon' },
    { label: 'Prayer Rankings', href: '/prayer-leaderboard', icon: 'TrophyIcon' },
    { label: 'Seniors Hub', href: '/seniors', icon: 'HeartIcon' },
    { label: 'My Dashboard', href: '/seniors/my-dashboard', icon: 'RectangleGroupIcon' },
    { label: 'Discover Scholars', href: '/seniors/discover-scholars', icon: 'MagnifyingGlassIcon' },
    { label: 'Sheikh Dashboard', href: '/sheikh/dashboard', icon: 'AcademicCapIcon', badge: sheikhUnreadCount },
    { label: 'Pending Questions', href: '/scholar/pending-questions', icon: 'InboxArrowDownIcon' },
    { label: 'Scholar Workspace', href: '/scholar/workspace', icon: 'PencilSquareIcon' },
    { label: 'Answer Editor', href: '/scholar/answer-editor', icon: 'DocumentTextIcon' },
    { label: 'Scholar Dashboard', href: '/scholar/my-dashboard', icon: 'ChartBarSquareIcon' },
    { label: 'Apply as Scholar', href: '/scholar/register', icon: 'UserPlusIcon' },
    { label: 'Ambassador', href: '/ambassador', icon: 'StarIcon' },
    { label: 'My Ambassador Dashboard', href: '/ambassador/dashboard', icon: 'UserCircleIcon' },
    { label: 'Apply as Ambassador', href: '/ambassador/apply', icon: 'UserPlusIcon' },
    { label: 'Ambassador Payouts', href: '/ambassador/payouts', icon: 'BanknotesIcon' },
    { label: 'Activity Feed', href: '/ambassador/activity', icon: 'BellIcon' },
    { label: 'Leaderboard', href: '/leaderboard', icon: 'TrophyIcon' },
    { label: 'Partner Analytics', href: '/admin/referrals', icon: 'ChartBarIcon' },
    { label: 'Admin Payouts', href: '/admin/payouts', icon: 'BanknotesIcon' },
    { label: 'Program Health', href: '/admin/ambassador-health', icon: 'HeartIcon' },
    { label: 'Answer Review', href: '/admin/scholar-answers', icon: 'ClipboardDocumentCheckIcon' },
    { label: 'Sheikh Directory', href: '/admin/sheikhs', icon: 'IdentificationIcon' },
    { label: 'Seniors Dashboard', href: '/admin/seniors-dashboard', icon: 'ChartPieIcon' },
    { label: 'Platform Dashboard', href: '/admin/platform-dashboard', icon: 'BuildingStorefrontIcon' },
    { label: 'Consultant Management', href: '/admin/consultant-management', icon: 'UserCircleIcon' },
    { label: 'Product Analytics', href: '/analytics', icon: 'PresentationChartLineIcon' },
    { label: 'Settings', href: '/settings', icon: 'Cog6ToothIcon' },
  ];

  return (
    <aside
      className="flex flex-col bg-card border-r border-border shadow-card transition-all duration-300 ease-in-out h-full"
      style={{ width: collapsed ? 64 : 240 }}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-border min-h-[72px]">
        <div className="flex-shrink-0">
          <AppLogo size={36} />
        </div>
        {!collapsed && (
          <span className="font-bold text-lg text-foreground tracking-tight truncate fade-in">
            Rafeeq
          </span>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="ml-auto p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors duration-150"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Icon
            name={collapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'}
            size={16}
          />
        </button>
      </div>

      {/* User Profile */}
      {!collapsed && (
        <div className="px-4 py-4 border-b border-border fade-in">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full gradient-teal flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
              AH
            </div>
            <div className="min-w-0">
              <p className="text-sm font-600 text-foreground truncate">Ahmed Hassan</p>
              <p className="text-xs text-muted-foreground truncate">ahmed@example.com</p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto scrollbar-thin">
        <div className="mb-2">
          {!collapsed && (
            <p className="px-3 text-xs font-500 text-muted-foreground uppercase tracking-widest mb-2">
              Menu
            </p>
          )}
          {navItems.map((item) => {
            const isActive = activeRoute === item.href;
            return (
              <Link
                key={`nav-${item.href}`}
                href={item.href}
                title={collapsed ? item.label : undefined}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 group relative ${
                  isActive
                    ? 'bg-primary/10 text-primary font-600' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <Icon
                  name={item.icon as Parameters<typeof Icon>[0]['name']}
                  size={20}
                  className={isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'}
                />
                {!collapsed && (
                  <span className="text-sm font-500 truncate fade-in">{item.label}</span>
                )}
                {item.badge && item.badge > 0 && (
                  <span className={`${collapsed ? 'absolute -top-1 -right-1' : 'ml-auto'} bg-accent text-accent-foreground text-xs font-600 px-1.5 py-0.5 rounded-full min-w-[20px] text-center`}>
                    {item.badge}
                  </span>
                )}
                {/* Tooltip for collapsed */}
                {collapsed && (
                  <span className="absolute left-full ml-2 px-2 py-1 bg-foreground text-background text-xs rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity duration-150">
                    {item.label}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Bottom Actions */}
      <div className="px-2 py-4 border-t border-border space-y-1">
        {bottomNavItems.map((item) => (
          <Link
            key={`bottom-nav-${item.href}`}
            href={item.href}
            title={collapsed ? item.label : undefined}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150 group relative"
          >
            <Icon
              name={item.icon as Parameters<typeof Icon>[0]['name']}
              size={20}
              className="text-muted-foreground group-hover:text-foreground"
            />
            {!collapsed && (
              <span className="text-sm font-500 truncate fade-in">{item.label}</span>
            )}
            {collapsed && (
              <span className="absolute left-full ml-2 px-2 py-1 bg-foreground text-background text-xs rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity duration-150">
                {item.label}
              </span>
            )}
          </Link>
        ))}
      </div>
    </aside>
  );
}